(() => {
  const boot = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tabs = Array.from(root.querySelectorAll('[data-scene-tab]'));
    const activateTab = (tab) => {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
        root.querySelector(`#${t.getAttribute('aria-controls')}`).hidden = !selected;
      });
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        let target;
        if (event.key === 'ArrowRight') target = tabs[(i + 1) % tabs.length];
        if (event.key === 'ArrowLeft') target = tabs[(i - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') target = tabs[0];
        if (event.key === 'End') target = tabs[tabs.length - 1];
        if (target) { event.preventDefault(); activateTab(target); target.focus(); }
      });
    });
    const catalog = root.querySelector('[data-catalog]');
    if (catalog) {
      const catalogTabs = Array.from(catalog.querySelectorAll('[data-catalog-tab]'));
      const catalogPanels = Array.from(catalog.querySelectorAll('[data-catalog-panel]'));
      const activateCategory = (tab) => {
        catalogTabs.forEach((button) => {
          const selected = button === tab;
          button.setAttribute('aria-selected', String(selected));
          button.tabIndex = selected ? 0 : -1;
        });
        catalogPanels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      };
      if (catalogTabs.length) activateCategory(catalogTabs[0]);
      catalogTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateCategory(tab));
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = catalogTabs[(index + 1) % catalogTabs.length];
          if (event.key === 'ArrowLeft') next = catalogTabs[(index - 1 + catalogTabs.length) % catalogTabs.length];
          if (event.key === 'Home') next = catalogTabs[0];
          if (event.key === 'End') next = catalogTabs[catalogTabs.length - 1];
          if (next) {
            event.preventDefault();
            activateCategory(next);
            next.focus();
            next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
          }
        });
      });
    }
    const dialogs = Array.from(root.querySelectorAll('dialog'));
    const openDialog = (dialog) => {
      if (!dialog.open) { dialog.showModal(); document.documentElement.style.overflow = 'hidden'; }
    };
    const closeDialog = (dialog) => {
      dialog.close();
    };
    dialogs.forEach((dialog) => {
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        const frame = dialog.querySelector('[data-video-frame]');
        if (frame) {
          const video = frame.querySelector('video');
          if (video) { video.pause(); video.removeAttribute('src'); video.querySelectorAll('source').forEach((source) => source.removeAttribute('src')); video.load(); }
          frame.replaceChildren();
        }
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(dialog);
        }
      });
    });
    const quickDialog = root.querySelector('[data-quick-dialog]');
    const openQuickView = (id) => {
      const template = root.querySelector(`#${id}`);
      if (!template) return;
      const content = quickDialog.querySelector('[data-quick-content]');
      content.replaceChildren(template.content.cloneNode(true));
      const select = content.querySelector('.ya-variant-select');
      if (select) select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        content.querySelector('.ya-quick-price').textContent = option.dataset.price;
        const link = content.querySelector('[data-variant-link]');
        link.href = `${link.dataset.productUrl}?variant=${option.value}`;
        if (option.dataset.image) {
          const image = content.querySelector('.ya-quick-image img');
          if (image) {
            image.removeAttribute('srcset');
            image.src = option.dataset.image;
          }
        }
      });
      openDialog(quickDialog);
    };
    root.addEventListener('click', (event) => {
      const close = event.target.closest('[data-close-dialog]');
      if (close) { closeDialog(close.closest('dialog')); return; }
      const quick = event.target.closest('[data-quick-view]');
      if (quick) { openQuickView(quick.dataset.quickView); return; }
      if (event.target.closest('[data-open-finder]')) { openDialog(root.querySelector('[data-finder-dialog]')); return; }
      if (event.target.closest('[data-open-video]')) {
        const dialog = root.querySelector('[data-video-dialog]');
        const frame = dialog.querySelector('[data-video-frame]');
        const hosted = dialog.querySelector('[data-hosted-video]');
        if (hosted) {
          frame.replaceChildren(hosted.content.cloneNode(true));
          const video = frame.querySelector('video');
          openDialog(dialog);
          video.play().catch(() => { /* Native controls remain available if autoplay is blocked. */ });
          return;
        }
        if (!frame.dataset.videoSrc) return;
        const iframe = document.createElement('iframe');
        iframe.src = frame.dataset.videoSrc;
        iframe.title = dialog.getAttribute('aria-label');
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        frame.replaceChildren(iframe);
        openDialog(dialog);
        return;
      }
      const hotspot = event.target.closest('[data-hotspot]');
      if (hotspot) {
        root.querySelectorAll('[data-hotspot]').forEach((button) => button.setAttribute('aria-pressed', String(button === hotspot)));
        root.querySelectorAll('[data-hotspot-panel]').forEach((panel) => { panel.hidden = panel.dataset.hotspotPanel !== hotspot.dataset.hotspot; });
        return;
      }
      const anchor = event.target.closest('a[href^="#"]');
      if (anchor) {
        const target = root.querySelector(anchor.getAttribute('href'));
        if (target) { event.preventDefault(); target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' }); }
      }
    });
    const sizeSelect = root.querySelector('[data-size-select]');
    const updateSize = () => {
      const option = sizeSelect.selectedOptions[0];
      if (!option) return;
      const sizes = [...option.dataset.size.matchAll(/(\d+)\s*ft/gi)].map((match) => Number(match[1]));
      if (sizes.length === 3) {
        root.querySelector('[data-size-width]').textContent = `${sizes[0]} ft · W`;
        root.querySelector('[data-size-height]').textContent = `${sizes[1]} ft · H`;
        root.querySelector('[data-size-depth]').textContent = `${sizes[2]} ft · D`;
        const diagram = root.querySelector('[data-size-diagram]');
        const visualSizes = {
          '10x10x10': [12, 11, 8.5],
          '10x8x8': [12, 9, 7],
          '10x8x5': [12, 9, 4.5],
          '8x8x8': [10, 9, 7],
          '8x8x5': [10, 9, 4.5]
        }[sizes.join('x')] || sizes;
        const w = visualSizes[0] * 18;
        const h = visualSizes[1] * 18;
        const dx = visualSizes[2] * 18 / Math.sqrt(2);
        const dy = dx;
        const left = 180;
        const bottom = 318;
        const right = left + w;
        const top = bottom - h;
        const backLeft = left + dx;
        const backRight = right + dx;
        const backBottom = bottom - dy;
        const backTop = top - dy;
        const set = (name, attribute, value) => diagram.querySelector(`[data-space-${name}]`)?.setAttribute(attribute, value);
        const back = `${backLeft},${backBottom} ${backRight},${backBottom} ${backRight},${backTop} ${backLeft},${backTop}`;
        set('floor', 'points', `${left},${bottom} ${right},${bottom} ${backRight},${backBottom} ${backLeft},${backBottom}`);
        set('left', 'points', `${left},${bottom} ${backLeft},${backBottom} ${backLeft},${backTop} ${left},${top}`);
        set('back-fill', 'points', back);
        set('back', 'points', back);
        set('right', 'points', `${right},${bottom} ${backRight},${backBottom} ${backRight},${backTop} ${right},${top}`);
        set('roof', 'points', `${left},${top} ${right},${top} ${backRight},${backTop} ${backLeft},${backTop}`);
        set('frame', 'd', `M${left} ${bottom}V${top}H${right}V${bottom}M${left} ${top}L${backLeft} ${backTop}H${backRight}L${right} ${top}M${backLeft} ${backTop}V${backBottom}H${backRight}V${backTop}M${right} ${bottom}L${backRight} ${backBottom}M${left} ${bottom}L${backLeft} ${backBottom}`);
        set('target', 'transform', `translate(${backLeft + w / 2} ${backTop + h * .58}) scale(${Math.min(w / 180, h / 180)})`);
        set('shadow', 'cx', left + w / 2 + dx / 2);
        set('shadow', 'cy', bottom - dy / 2 + 12);
        set('shadow', 'rx', (w + dx) / 2 + 18);
        set('width-line', 'd', `M${left} 351H${right}`);
        set('height-line', 'd', `M156 ${top}V${bottom}`);
        set('depth-line', 'd', `M${right + 24} ${bottom + 2}L${backRight + 24} ${backBottom + 2}`);
        set('extensions', 'd', `M${left} 325V361M${right} 325V361M162 ${top}H173M162 ${bottom}H173M${right + 12} ${bottom}L${right + 31} ${bottom + 11}M${backRight + 12} ${backBottom}L${backRight + 31} ${backBottom + 11}`);
        const widthLabel = root.querySelector('[data-size-width]');
        const heightLabel = root.querySelector('[data-size-height]');
        const depthLabel = root.querySelector('[data-size-depth]');
        widthLabel.setAttribute('x', left + w / 2);
        heightLabel.setAttribute('y', bottom - h / 2);
        heightLabel.setAttribute('transform', `rotate(-90 136 ${bottom - h / 2})`);
        depthLabel.setAttribute('x', right + dx / 2 + 50);
        depthLabel.setAttribute('y', bottom - dy / 2 + 22);
      }
      root.querySelector('[data-size-caption]').textContent = option.dataset.size;
      const link = root.querySelector('[data-size-link]');
      link.href = `${link.dataset.productUrl}?variant=${option.value}`;
    };
    if (sizeSelect) { sizeSelect.addEventListener('change', updateSize); updateSize(); }
    const pause = root.querySelector('[data-marquee-pause]');
    if (pause) pause.addEventListener('click', () => {
      const paused = pause.getAttribute('aria-pressed') !== 'true';
      pause.setAttribute('aria-pressed', String(paused));
      root.querySelector('[data-presence-marquee]').dataset.paused = String(paused);
      pause.querySelector('[data-marquee-symbol]').textContent = paused ? '▷' : 'Ⅱ';
      const label = pause.querySelector('[data-marquee-label]');
      label.textContent = paused ? label.dataset.resume : label.dataset.pause;
    });
    const form = root.querySelector('[data-finder-form]');
    if (form) form.addEventListener('submit', (event) => {
      event.preventDefault();
      const goal = form.elements.goal.value;
      const space = form.elements.space.value;
      const budget = Number(form.elements.budget.value);
      const strings = root.querySelector('[data-finder-strings]').dataset;
      const seen = new Set();
      const candidates = Array.from(root.querySelectorAll('.ya-product-grid .ya-card')).filter((card) => {
        if (card.dataset.goal !== goal || card.dataset.available === 'false' || seen.has(card.dataset.url)) return false;
        seen.add(card.dataset.url);
        return true;
      }).sort((a, b) => Number(!!a.closest('[data-catalog]')) - Number(!!b.closest('[data-catalog]')) || Number(a.dataset.price) - Number(b.dataset.price));
      const matches = candidates.filter((card) => Number(card.dataset.price) <= budget).slice(0, 2);
      const shown = matches.length ? matches : candidates.slice(0, 1);
      const result = root.querySelector('[data-finder-result]');
      let summary = matches.length ? strings.match : strings.budgetMiss;
      if ((space === 'indoor' || space === 'garage') && goal !== 'putting') summary += ` ${strings.indoor}`;
      root.querySelector('[data-finder-summary]').textContent = summary;
      const links = root.querySelector('[data-finder-links]');
      links.replaceChildren();
      shown.forEach((card) => {
        const link = document.createElement('a');
        link.className = 'ya-text-link';
        link.href = card.dataset.url;
        link.textContent = `${card.dataset.title} ↗`;
        links.append(link);
      });
      result.hidden = false;
      result.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
    });
    if ('IntersectionObserver' in window && !reduced) {
      root.classList.add('ya-motion');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('ya-visible'); observer.unobserve(entry.target); } });
      }, { threshold: .08 });
      root.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
    }
  };
  const start = () => document.querySelectorAll('[data-alive-home]').forEach(boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();






